import { useEffect, useRef } from 'react';

/**
 * React hook that automatically removes Spline logo elements from the DOM
 * 
 * This hook:
 * - Removes any existing elements matching #logo or a[href*="spline.design"] on mount
 * - Sets up a MutationObserver to continuously monitor and remove these elements
 * - Is production-ready and handles cleanup properly
 * 
 * @returns void - This hook only performs side effects
 */
export const useKillSplineLogo = () => {
  const observerRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    console.log('🚀 SPLINE LOGO KILLER: Starting mission to eliminate Spline logos!');
    
    // Selectors to target all possible Spline logo variations
    const selectors = [
      'a#logo[href*="spline.design"]',
      'a[href*="spline.design"]',
      'a[href*="spline"]',
      '.spline-logo',
      '.spline-branding',
      '.spline-attribution',
      '[data-spline-logo]',
      'spline-viewer a#logo',
      'spline-viewer a[href*="spline.design"]',
      'spline-viewer a',
      // Additional aggressive selectors
      'a[id="logo"]',
      'a[href*="spline.design"]',
      'a[href*="spline"]'
    ];

    /**
     * Aggressively removes all Spline logo elements from the DOM
     */
    const removeSplineLogos = () => {
      let totalRemoved = 0;
      
      // First, try to find and remove from shadow DOM
      const splineViewers = document.querySelectorAll('spline-viewer');
      splineViewers.forEach(viewer => {
        try {
          // Access shadow root if it exists
          if (viewer.shadowRoot) {
            const shadowLogos = viewer.shadowRoot.querySelectorAll('a[href*="spline"], a#logo');
            shadowLogos.forEach(logo => {
              console.log('💀 ELIMINATING SHADOW DOM SPLINE LOGO:', {
                element: logo.outerHTML,
                href: logo.href
              });
              logo.remove();
              totalRemoved++;
            });
          }
        } catch (error) {
          console.warn('Error accessing shadow DOM:', error);
        }
      });
      
      // Then try regular DOM selectors
      selectors.forEach(selector => {
        try {
          const elements = document.querySelectorAll(selector);
          elements.forEach(element => {
            // Additional check to ensure it's actually a Spline logo
            if (element instanceof HTMLAnchorElement) {
              const href = element.getAttribute('href') || '';
              const id = element.getAttribute('id') || '';
              
              if (href.includes('spline.design') || 
                  (id === 'logo' && href.includes('spline'))) {
                console.log('💀 ELIMINATING SPLINE LOGO:', {
                  selector,
                  href,
                  id,
                  element: element.outerHTML
                });
                element.remove();
                totalRemoved++;
              }
            } else {
              // Remove non-anchor elements that match our selectors
              console.log('💀 ELIMINATING SPLINE ELEMENT:', {
                selector,
                element: element.outerHTML
              });
              element.remove();
              totalRemoved++;
            }
          });
        } catch (error) {
          console.warn('Error removing Spline logos:', error);
        }
      });
      
      // Also try to find any element with spline in the href
      try {
        const allLinks = document.querySelectorAll('a');
        allLinks.forEach(link => {
          const href = link.getAttribute('href') || '';
          if (href.includes('spline.design')) {
            console.log('💀 ELIMINATING SPLINE LINK:', {
              href,
              element: link.outerHTML
            });
            link.remove();
            totalRemoved++;
          }
        });
      } catch (error) {
        console.warn('Error scanning all links:', error);
      }
      
      if (totalRemoved > 0) {
        console.log(`🎯 MISSION ACCOMPLISHED: Eliminated ${totalRemoved} Spline logo(s)!`);
      }
    };

    // Inject aggressive CSS to hide logos
    const injectBlockingCSS = () => {
      const existingStyle = document.getElementById('spline-logo-killer-css');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      const style = document.createElement('style');
      style.id = 'spline-logo-killer-css';
      style.textContent = `
        /* AGGRESSIVE SPLINE LOGO BLOCKING */
        a#logo[href*="spline.design"],
        a[href*="spline.design"][id="logo"],
        a[href*="spline.design"],
        a[href*="spline"],
        a[id="logo"][href*="spline"],
        .spline-logo,
        .spline-branding,
        .spline-attribution,
        [data-spline-logo],
        spline-viewer a#logo,
        spline-viewer a[href*="spline.design"],
        spline-viewer a,
        /* Shadow DOM targeting */
        spline-viewer::shadow a#logo,
        spline-viewer::shadow a[href*="spline.design"],
        spline-viewer::shadow a[href*="spline"],
        /* Deep targeting */
        spline-viewer * a#logo,
        spline-viewer * a[href*="spline.design"],
        spline-viewer * a[href*="spline"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
          position: absolute !important;
          left: -9999px !important;
          top: -9999px !important;
          width: 0 !important;
          height: 0 !important;
          overflow: hidden !important;
          z-index: -9999 !important;
          clip: rect(0,0,0,0) !important;
          clip-path: inset(50%) !important;
        }
      `;
      document.head.appendChild(style);
      console.log('🛡️ CSS SHIELD DEPLOYED: Spline logos are now blocked by CSS!');
    };

    // Deploy CSS shield immediately
    injectBlockingCSS();

    // Remove logos immediately on mount
    removeSplineLogos();

    // Set up MutationObserver to catch dynamically added logos
    observerRef.current = new MutationObserver((mutations) => {
      let shouldRemove = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node;
              
              // Check if the added node itself is a logo
              if (element.matches && selectors.some(selector => element.matches(selector))) {
                shouldRemove = true;
                return;
              }
              
              // Check if any child elements are logos
              if (element.querySelector) {
                const hasLogos = selectors.some(selector => element.querySelector(selector));
                if (hasLogos) {
                  shouldRemove = true;
                }
              }
            }
          });
        }
      });
      
      if (shouldRemove) {
        // Use requestAnimationFrame to avoid blocking the main thread
        requestAnimationFrame(removeSplineLogos);
      }
    });

    // Start observing the entire document
    observerRef.current.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'id', 'class']
    });

    // Set up periodic cleanup as a fallback - more aggressive
    intervalRef.current = setInterval(removeSplineLogos, 100);

    // Cleanup function
    return () => {
      console.log('🧹 CLEANUP: Shutting down Spline logo killer...');
      
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Remove the CSS shield
      const existingStyle = document.getElementById('spline-logo-killer-css');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount
};
